/* Nimbus header — sticky scroll-up, transparent-over-hero, drawer + submenus, search overlay, cart drawer trigger */
(() => {
  class NimbusHeader extends HTMLElement {
    connectedCallback() {
      this.sticky = this.dataset.sticky === 'true';
      this.drawer = document.getElementById('nimbus-mobile-drawer');
      this.search = document.getElementById('nimbus-search-overlay');
      this.cartDrawer = document.querySelector('cart-drawer, [data-nimbus-cart-drawer]');

      this.drawerToggles = this.querySelectorAll('[data-drawer-toggle]');
      this.drawerCloses = this.drawer ? this.drawer.querySelectorAll('[data-drawer-close]') : [];
      this.searchToggles = this.querySelectorAll('[data-search-toggle]');
      this.searchCloses = this.search ? this.search.querySelectorAll('[data-search-close]') : [];
      this.subToggles = this.drawer ? this.drawer.querySelectorAll('[data-drawer-sub-toggle]') : [];
      this.cartTriggers = this.querySelectorAll('[data-cart-toggle]');

      this.lastScroll = 0;
      this.heroEl = document.querySelector('.nimbus-hero, .sb-hero, [data-hero-sentinel]');

      /* ── Announcement bar scrolls away, main bar stays sticky ──────────
       * The sticky group offsets by the announce bar's real height (CSS:
       * top: calc(-1 * var(--nimbus-announce-h))). Measure it here so the
       * offset always matches, whatever the bar's content or font size. */
      const announce = this.querySelector('.nimbus-header__announce');
      const stickyGroup = this.closest('.shopify-section-group-header-group');
      if (announce && stickyGroup) {
        const setAnnounceHeight = () => {
          stickyGroup.style.setProperty('--nimbus-announce-h', `${announce.offsetHeight}px`);
        };
        setAnnounceHeight();
        window.addEventListener('resize', setAnnounceHeight);
      }

      /* ── IntersectionObserver for is-past-hero ─────────────────────────
       * transparent_over_hero is a global setting. On pages without a hero
       * (all inner pages — About, Collection, Product, etc.) there is no
       * sentinel element to observe, so is-past-hero would never be added
       * and the header would stay transparent for the entire page.
       * Fix: if no sentinel is found, immediately set is-past-hero so the
       * header renders with its opaque / glassy style on non-homepage pages.
       * ─────────────────────────────────────────────────────────────────── */
      if (this.classList.contains('nimbus-header--transparent')) {
        const bannerEl = document.querySelector('.nimbus-logo-banner') || this.heroEl;
        if (bannerEl) {
          this._heroObserver = new IntersectionObserver(([entry]) => {
            this.classList.toggle('is-past-hero', !entry.isIntersecting);
          }, { rootMargin: '-24px 0px 0px 0px', threshold: 0 });
          this._heroObserver.observe(bannerEl);
        } else {
          // No hero on this page — act as if already past the hero.
          this.classList.add('is-past-hero');
        }
      }

      this.drawerToggles.forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); this.openDrawer(); }));
      this.drawerCloses.forEach(el => el.addEventListener('click', () => this.closeDrawer()));
      this.searchToggles.forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); this.openSearch(); }));
      this.searchCloses.forEach(el => el.addEventListener('click', () => this.closeSearch()));

      this.subToggles.forEach(btn => btn.addEventListener('click', e => this.toggleSub(e, btn)));

      /* ── Mega menu ── */
      this.megaItems = Array.from(this.querySelectorAll('.nimbus-header__nav-item.has-mega'));
      this.megaBackdrop = this.querySelector('[data-mega-backdrop]');
      this.megaItems.forEach(item => {
        const trigger = item.querySelector('[data-mega-trigger]');
        if (trigger) {
          trigger.addEventListener('click', () => this.toggleMega(item));
        }
        // Hover-to-open + delayed close so mouse can travel into the panel.
        //
        // Architecture note: .has-mega items are position:static (base.css) so the
        // mega panel positions relative to the sticky <header>, NOT the <li>.
        // That creates a gap between the <li> hit-box and the mega panel top edge.
        // Fixing this purely with CSS :hover is not possible — as soon as the mouse
        // crosses the gap, :hover on the <li> is lost and CSS instantly sets
        // pointer-events:none on the panel, preventing the panel's mouseenter from
        // firing to cancel the close timer.
        //
        // Solution: open via JS openMega() on mouseenter (adds .is-open), which keeps
        // pointer-events:all alive throughout the 300 ms delay window. The panel's
        // mouseenter then reliably cancels the timer before the mega closes.
        let leaveTimer;
        const cancelClose  = () => clearTimeout(leaveTimer);
        const scheduleClose = () => { leaveTimer = setTimeout(() => this.closeMega(item), 300); };

        item.addEventListener('mouseenter', () => {
          cancelClose();
          // Close any sibling mega that might be open
          this.megaItems.forEach(other => { if (other !== item) this.closeMega(other); });
          this.openMega(item);
        });
        item.addEventListener('mouseleave', scheduleClose);

        const panel = item.querySelector('[data-mega-panel]');
        if (panel) {
          panel.addEventListener('mouseenter', cancelClose);
          panel.addEventListener('mouseleave', scheduleClose);
        }
      });
      // Close on backdrop click
      if (this.megaBackdrop) {
        this.megaBackdrop.addEventListener('click', () => this.closeAllMega());
      }

      /* Cart drawer: let native cart-drawer handle if exists, else fall through to href */
      this.cartTriggers.forEach(el => {
        el.addEventListener('click', e => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            e.preventDefault();
            drawer.open();
          }
        });
      });

      document.addEventListener('keydown', e => {
        if (e.key !== 'Escape') return;
        if (this.drawer && this.drawer.classList.contains('is-open')) this.closeDrawer();
        if (this.search && this.search.classList.contains('is-open')) this.closeSearch();
        this.closeAllMega();
      });

      this._onScroll = this._onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    openDrawer() {
      if (!this.drawer) return;
      /* Teleport drawer to body — fully escapes header stacking context/transform */
      this._drawerParent = this.drawer.parentNode;
      document.body.appendChild(this.drawer);
      document.body.classList.add('nimbus-drawer-open');
      this.drawer.removeAttribute('hidden');
      this.drawer.setAttribute('aria-hidden', 'false');
      /* Force reflow so browser commits opacity:0 before transition */
      const panel = this.drawer.querySelector('.nimbus-drawer__panel');
      if (panel) void panel.offsetWidth;
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      this.drawerToggles.forEach(btn => btn.setAttribute('aria-expanded', 'true'));
      const first = this.drawer.querySelector('a, button');
      if (first) setTimeout(() => first.focus(), 500);
    }

    closeDrawer() {
      if (!this.drawer) return;
      /* a11y: never aria-hide an element that still contains focus — move focus
         back to the burger first (or blur), otherwise screen readers lose it */
      if (this.drawer.contains(document.activeElement)) {
        const toggle = this.drawerToggles && this.drawerToggles[0];
        if (toggle) { toggle.focus(); } else { document.activeElement.blur(); }
      }
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('nimbus-drawer-open');
      this.drawerToggles.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
      /* Wait for exit animation, then hide and teleport back into header */
      setTimeout(() => {
        if (!this.drawer.classList.contains('is-open')) {
          this.drawer.setAttribute('hidden', '');
          if (this._drawerParent) this._drawerParent.appendChild(this.drawer);
        }
      }, 560);
    }

    openSearch() {
      if (!this.search) return;
      this.search.removeAttribute('hidden');
      this.search.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => this.search.classList.add('is-open'));
      document.body.classList.add('nimbus-drawer-open');
      this.searchToggles.forEach(btn => btn.setAttribute('aria-expanded', 'true'));
      const input = this.search.querySelector('[data-search-input]');
      if (input) setTimeout(() => input.focus(), 260);
    }

    closeSearch() {
      if (!this.search) return;
      this.search.classList.remove('is-open');
      this.search.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('.nimbus-drawer.is-open')) document.body.classList.remove('nimbus-drawer-open');
      this.searchToggles.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
      setTimeout(() => { if (!this.search.classList.contains('is-open')) this.search.setAttribute('hidden', ''); }, 360);
    }

    toggleSub(event, btn) {
      event.preventDefault();
      const item = btn.closest('.nimbus-drawer__item');
      if (!item) return;
      const sub = item.querySelector('.nimbus-drawer__sub');
      if (!sub) return;
      const isOpen = item.classList.contains('is-open');
      /* Accordion: close other open items */
      this.drawer.querySelectorAll('.nimbus-drawer__item.is-open').forEach(open => {
        if (open !== item) {
          open.classList.remove('is-open');
          const s = open.querySelector('.nimbus-drawer__sub');
          const b = open.querySelector('[data-drawer-sub-toggle]');
          if (s) s.setAttribute('hidden', '');
          if (b) b.setAttribute('aria-expanded', 'false');
        }
      });
      if (isOpen) {
        item.classList.remove('is-open');
        sub.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      } else {
        item.classList.add('is-open');
        sub.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
      }
    }

    toggleMega(item) {
      const isOpen = item.classList.contains('is-open');
      this.closeAllMega();
      if (!isOpen) this.openMega(item);
    }

    openMega(item) {
      item.classList.add('is-open');
      const trigger = item.querySelector('[data-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (this.megaBackdrop) this.megaBackdrop.classList.add('is-visible');
    }

    closeMega(item) {
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      // Hide backdrop only if no other mega is open
      if (!this.megaItems.some(i => i.classList.contains('is-open'))) {
        if (this.megaBackdrop) this.megaBackdrop.classList.remove('is-visible');
      }
    }

    closeAllMega() {
      this.megaItems.forEach(item => this.closeMega(item));
      if (this.megaBackdrop) this.megaBackdrop.classList.remove('is-visible');
    }

    _onScroll() {
      const y = window.pageYOffset || document.documentElement.scrollTop;
      const scrolled = y > 24;
      this.classList.toggle('is-scrolled', scrolled);

      /* ── Past-hero handled by IntersectionObserver above ── */

      if (this.sticky) {
        /* Only hide the header when already past the hero section.
           While the morph is active (transparent header, not past-hero)
           the header must stay visible regardless of scroll direction. */
        const pastHero = this.classList.contains('is-past-hero');
        if (y > this.lastScroll + 4 && y > 140 && pastHero) {
          this.classList.add('is-stuck-hidden');
        } else if (y < this.lastScroll - 4) {
          this.classList.remove('is-stuck-hidden');
        }
        this.classList.toggle('is-stuck', scrolled);
      }
      this.lastScroll = y;
    }
  }

  if (!customElements.get('nimbus-header')) {
    customElements.define('nimbus-header', NimbusHeader);
  }

  /* Cart count live updater — listens for Shopify's native fetch/cart/add events via PubSub (Dawn) */
  document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('cart:refresh', refreshCount);
    if (window.PUB_SUB_EVENTS && window.subscribe) {
      window.subscribe(PUB_SUB_EVENTS.cartUpdate || 'cart-update', refreshCount);
    }
  });

  async function refreshCount() {
    try {
      const res = await fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js');
      const data = await res.json();
      const badge = document.querySelector('[data-cart-count]');
      if (!badge) return;
      badge.textContent = data.item_count;
      badge.classList.toggle('is-empty', data.item_count === 0);
    } catch (e) { /* silent */ }
  }

  /* ── Cart link in menu drawer → sluit menu, open cart drawer ── */
  document.addEventListener('click', function(e) {
    var link = e.target.closest('.nimbus-drawer__meta a[href*="cart"]');
    if (!link) return;
    e.preventDefault();
    /* Sluit menu drawer */
    var nimbusHeader = document.querySelector('nimbus-header');
    if (nimbusHeader && typeof nimbusHeader.closeDrawer === 'function') {
      nimbusHeader.closeDrawer();
    }
    /* Open cart drawer na exit-animatie */
    setTimeout(function() {
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }, 320);
  });

})();
